import React, { useEffect, useState } from "react";
import { Modal, Input, Checkbox, Button, Select, Alert, Radio } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getOversightCompanies } from "api/oversight/oversightApi";
import { matchOption } from "components/SearchSelect/searchSelect";

// THE SUPER ADMIN'S EDITOR FOR ONE OVERSIGHT ENTRY (owner brief 2026-10-11
// T39-T40): the overseeing company (when adding), its members by name and
// email, the one lead, and each member's permissions ticked one by one. Only
// rendered when the server answered canEdit; the server refuses the save for
// anybody else anyway.
//
// `entry` null = add a new entry. onSave(body) receives
// { companyId?, companyName?, members: [{ name, email, lead, permissions }] }.
const blankMember = () => ({ name: "", email: "", lead: false, permissions: [] });

export default function OversightEditor({ open, projectId, entry, catalog = [], onCancel, onSave }) {
  const adding = !entry;
  const [companies, setCompanies] = useState([]);
  const [ownerCompanyId, setOwnerCompanyId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    setSaving(false);
    setCompanyId(entry ? String(entry.companyId || "") : "");
    setMembers(entry
      ? (entry.members || []).map((m) => ({ name: m.name || "", email: m.email || "", lead: !!m.lead, permissions: [...(m.permissions || [])] }))
      : [blankMember()]);
    if (!adding) return undefined;
    let dead = false;
    getOversightCompanies(projectId)
      .then((d) => {
        if (dead) return;
        setCompanies(Array.isArray(d && d.companies) ? d.companies : []);
        setOwnerCompanyId(String((d && d.ownerCompanyId) || ""));
      })
      .catch(() => { if (!dead) setCompanies([]); });
    return () => { dead = true; };
  }, [open, entry, adding, projectId]);

  const setMember = (i, patch) => setMembers((list) => list.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  // One lead per entry: choosing a lead clears it from everyone else.
  const makeLead = (i) => setMembers((list) => list.map((m, j) => ({ ...m, lead: j === i })));
  const noLead = () => setMembers((list) => list.map((m) => ({ ...m, lead: false })));
  const leadIndex = members.findIndex((m) => m.lead);

  const save = async () => {
    setError("");
    if (adding && !companyId) { setError("Pick the overseeing company."); return; }
    const clean = members.map((m) => ({ ...m, name: m.name.trim(), email: m.email.trim().toLowerCase() }));
    if (clean.some((m) => !m.name || !m.email)) { setError("Every member needs a name and an email."); return; }
    const body = { members: clean.map((m) => ({ name: m.name, email: m.email, lead: m.lead, permissions: m.lead ? [] : m.permissions })) };
    if (adding) {
      body.companyId = companyId;
      const c = companies.find((x) => x._id === companyId);
      if (c) body.companyName = c.name;
    }
    setSaving(true);
    try {
      await onSave(body);
    } catch (e) {
      setError((e && e.message) || "The entry could not be saved.");
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={adding ? "Add oversight entry" : `Edit ${entry.label}`}
      onCancel={onCancel}
      onOk={save}
      okText="Save"
      confirmLoading={saving}
      width={860}
      destroyOnClose
    >
      <div style={{ color: "#667085", fontSize: 13, marginBottom: 12 }}>
        The lead has every power of the project owner on this project. Each other member has only the permissions ticked
        below. Every change here is written to the oversight activity log.
      </div>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
      {adding ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Overseeing company</div>
          <Select filterOption={matchOption}
            showSearch
            style={{ width: "100%" }}
            placeholder="Choose a company"
            value={companyId || undefined}
            onChange={setCompanyId}
            optionFilterProp="label"
            options={companies.map((c) => ({ value: c._id, label: c.name }))}
            aria-label="Overseeing company"
          />
          {companyId && companyId === ownerCompanyId ? (
            <div style={{ color: "#B54708", fontSize: 12.5, marginTop: 4 }}>
              This company owns the project now. The entry is saved but takes effect only when the project moves to
              another company.
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontWeight: 600 }}>Members</div>
        <Button size="small" onClick={noLead} disabled={leadIndex < 0}>No lead</Button>
      </div>
      {members.map((m, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} data-testid="oversight-member" style={{ border: "1px solid #E4E7EC", borderRadius: 8, padding: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Input
              style={{ flex: "1 1 180px" }}
              placeholder="Name"
              aria-label={`Member ${i + 1} name`}
              value={m.name}
              onChange={(e) => setMember(i, { name: e.target.value })}
            />
            <Input
              style={{ flex: "1 1 220px" }}
              placeholder="Email"
              aria-label={`Member ${i + 1} email`}
              value={m.email}
              onChange={(e) => setMember(i, { email: e.target.value })}
            />
            <Radio checked={m.lead} onChange={() => makeLead(i)} aria-label={`Member ${i + 1} is the lead`}>Lead</Radio>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Remove member ${i + 1}`}
              onClick={() => setMembers((list) => list.filter((_, j) => j !== i))}
            />
          </div>
          {m.lead ? (
            <div style={{ color: "#667085", fontSize: 12.5, marginTop: 8 }}>Every project owner permission.</div>
          ) : (
            <Checkbox.Group
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "4px 12px", marginTop: 8 }}
              value={m.permissions}
              onChange={(v) => setMember(i, { permissions: v })}
              options={catalog.map((c) => ({ value: c.key, label: c.label }))}
            />
          )}
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => setMembers((list) => [...list, blankMember()])}>Add member</Button>
    </Modal>
  );
}
